// Email service for sending application data using PHP backend
export interface ApplicationData {
  vehicleType: string;
  monthlyBudget: string;
  hasTradeIn: string;
  employmentStatus: string;
  monthlyIncome: string;
  address: string;
  firstName: string;
  dateOfBirth: string;
  email: string;
  phone: string;
  website?: string; // Honeypot field
  confirmEmail?: string; // Honeypot field
}

export interface EmailResult {
  success: boolean;
  message: string;
}

// Email service using internal PHP API
export class EmailService {
  // Get API base URL
  private static getApiUrl(): string {
    // In production, this should be your domain
    const isDevelopment = process.env.NODE_ENV === 'development';
    if (isDevelopment) {
      // For local development, use the same port as React app
      return 'http://localhost:3000/php/api';
    }
    return '/php/api'; // Relative path for production
  }

  // Send application data via PHP API
  static async sendApplication(applicationData: ApplicationData): Promise<EmailResult> {
    try {
      const apiUrl = `${this.getApiUrl()}/sub_application.php`;
      
      console.log('Attempting to send application to:', apiUrl);
      console.log('Application data:', applicationData);
      
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(applicationData),
      });

      console.log('Response status:', response.status);
      console.log('Response headers:', response.headers);
      
      // Check if response is actually JSON
      const contentType = response.headers.get('content-type');
      console.log('Content-Type:', contentType);
      
      if (!contentType || !contentType.includes('application/json')) {
        const textResponse = await response.text();
        console.log('Non-JSON response:', textResponse);
        return {
          success: false,
          message: `Server returned non-JSON response. This usually means PHP has an error. Response: ${textResponse.substring(0, 200)}`
        };
      }
      
      const result = await response.json();
      console.log('Response data:', result);

      if (response.ok && result.success) {
        return {
          success: true,
          message: result.message || '🎉 Congratulations! You Are Pre-Approved! One of our approval specialists will contact you very shortly with the best prices and rates.'
        };
      } else {
        return {
          success: false,
          message: result.message || 'Failed to submit application. Please try again.'
        };
      }
    } catch (error) {
      console.error('Email sending failed:', error);
      
      // Provide more specific error messages
      if (error instanceof TypeError && error.message.includes('fetch')) {
        return {
          success: false,
          message: 'Cannot connect to server. Please check if the PHP backend is running and accessible.'
        };
      } else if (error instanceof SyntaxError) {
        return {
          success: false,
          message: 'Server returned invalid response. Please check the PHP backend configuration.'
        };
      } else {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        return {
          success: false,
          message: `Failed to submit application: ${errorMessage}`
        };
      }
    }
  }

  // Test API connection
  static async testApiConnection(): Promise<EmailResult> {
    try {
      const apiUrl = `${this.getApiUrl()}/minimal_test.php`;
      console.log('Testing API connection to:', apiUrl);
      
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ test: 'data' })
      });

      console.log('Test response status:', response.status);
      console.log('Test response headers:', response.headers);
      
      // Check if response is actually JSON
      const contentType = response.headers.get('content-type');
      console.log('Content-Type:', contentType);
      
      if (!contentType || !contentType.includes('application/json')) {
        const textResponse = await response.text();
        console.log('Non-JSON response:', textResponse);
        return {
          success: false,
          message: `Server returned non-JSON response: ${textResponse.substring(0, 100)}`
        };
      }
      
      const result = await response.json();
      console.log('Test response data:', result);

      if (response.ok && result.success) {
        return {
          success: true,
          message: 'API connection successful'
        };
      } else {
        return {
          success: false,
          message: 'API connection failed'
        };
      }
    } catch (error) {
      console.error('API test failed:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      return {
        success: false,
        message: `API connection test failed: ${errorMessage}`
      };
    }
  }

  // Alternative method using mailto link (fallback)
  static createMailtoLink(applicationData: ApplicationData): string {
    const subject = encodeURIComponent(`Car Loan Application from ${applicationData.firstName}`);
    const body = encodeURIComponent(`
New Car Loan Application

Personal Information:
- Name: ${applicationData.firstName}
- Email: ${applicationData.email}
- Phone: ${applicationData.phone}

Vehicle Information:
- Vehicle Type: ${applicationData.vehicleType}
- Monthly Budget: ${applicationData.monthlyBudget}
- Has Trade-in: ${applicationData.hasTradeIn}

Financial Information:
- Employment Status: ${applicationData.employmentStatus}
- Monthly Income: ${applicationData.monthlyIncome}

Application Details:
- Submitted: ${new Date().toLocaleString()}

Please contact this applicant as soon as possible.
    `.trim());
    return `mailto:your-email@example.com?subject=${subject}&body=${body}`;
  }

  // Send email using mailto (opens user's email client) - fallback method
  static sendViaMailto(applicationData: ApplicationData): void {
    const mailtoLink = this.createMailtoLink(applicationData);
    window.open(mailtoLink, '_blank');
  }

  // Test database connection specifically
  static async testDatabaseConnection(): Promise<EmailResult> {
    try {
      const apiUrl = `${this.getApiUrl()}/database_test.php`;
      console.log('Testing database connection to:', apiUrl);
      
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ test: 'database' })
      });

      console.log('Database test response status:', response.status);
      
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const textResponse = await response.text();
        console.log('Non-JSON response:', textResponse);
        return {
          success: false,
          message: `Database test failed - non-JSON response: ${textResponse.substring(0, 200)}`
        };
      }
      
      const result = await response.json();
      console.log('Database test response:', result);

      if (response.ok && result.success) {
        return {
          success: true,
          message: 'Database connection successful'
        };
      } else {
        return {
          success: false,
          message: result.message || 'Database connection failed'
        };
      }
    } catch (error) {
      console.error('Database test failed:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      return {
        success: false,
        message: `Database test failed: ${errorMessage}`
      };
    }
  }
}
